import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

/* --- helpers ----------------------------------------------- */
const PAGE_SIZES = [10, 25, 50, 100];
const calcEdad = fn => {
  if (!fn) return '—';
  const y = Math.floor((Date.now() - new Date(fn)) / 3.156e10);
  return y + ' años';
};
const fmtFecha = fn => fn ? new Date(fn).toLocaleDateString('es-EC') : '—';
const iniciales = n => n ? n.trim().split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase() : '';

// Colores del badge de Tipo de paciente en la lista — mismos tonos que la
// caja morada de TIPO_PACIENTE ya usada en la ficha (.tipo-pac-box).
const TIPO_BADGE = {
  PARTICULAR: { l: 'Particular', bg: '#eef1f5', fg: '#556072' },
  CONVENIO:   { l: 'Convenio',   bg: 'var(--blue-lt)', fg: 'var(--navy)' },
  SEGURO:     { l: 'Seguro',     bg: '#f1ecf9', fg: '#6b4e97' },
  PROGRAMA:   { l: 'Programa',   bg: '#fdf3e2', fg: '#b8760f' },
};

// Edad detallada (años, meses y días) — igual al criterio pedido para
// la ficha del paciente, complementa a calcEdad() (que sigue usándose
// en la lista/tarjetas, donde solo cabe el número de años).
const calcEdadDetallada = fn => {
  if (!fn) return null;
  const nac = new Date(fn.length > 10 ? fn.substring(0,10) : fn);
  const hoy = new Date();
  let anios = hoy.getFullYear() - nac.getFullYear();
  let meses = hoy.getMonth() - nac.getMonth();
  let dias  = hoy.getDate() - nac.getDate();
  if (dias < 0) { meses--; dias += new Date(hoy.getFullYear(), hoy.getMonth(), 0).getDate(); }
  if (meses < 0) { anios--; meses += 12; }
  return { anios, meses, dias, totalMeses: anios*12 + meses };
};
const fmtEdadDetallada = fn => {
  const e = calcEdadDetallada(fn);
  if (!e) return '';
  return `${e.anios} años, ${e.meses} meses y ${e.dias} días (${e.totalMeses} meses)`;
};

// Link directo de WhatsApp a partir de un celular ecuatoriano (09XXXXXXXX).
// No requiere backend — abre la conversación directamente en WhatsApp.
const waLink = tel => {
  const digitos = (tel || '').replace(/\D/g, '');
  if (!/^0\d{9}$/.test(digitos)) return null;
  return `https://wa.me/593${digitos.slice(1)}`;
};

// Ícono real de WhatsApp (antes emoji 💬) — SVG en línea, hereda el color
// del botón vía currentColor, sin depender de ninguna fuente de íconos externa.
const WaIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.9.5 3.66 1.4 5.19L2 22l5.06-1.5a9.9 9.9 0 0 0 4.98 1.34h.01c5.46 0 9.9-4.45 9.9-9.91C21.96 6.45 17.5 2 12.04 2zm5.8 14.13c-.24.68-1.4 1.3-1.93 1.37-.5.07-1.12.1-1.8-.11-.42-.13-.95-.3-1.63-.6-2.87-1.24-4.74-4.13-4.88-4.32-.14-.19-1.17-1.55-1.17-2.96s.73-2.1.99-2.39c.25-.28.55-.35.74-.35.18 0 .37 0 .53.01.17.01.4-.07.62.47.24.57.8 1.98.87 2.13.07.14.11.31.02.5-.09.19-.14.31-.28.48-.14.17-.29.38-.42.5-.14.14-.28.28-.12.55.16.28.7 1.16 1.51 1.88 1.04.93 1.92 1.22 2.19 1.36.28.14.44.12.6-.07.17-.18.71-.83.9-1.11.19-.28.38-.23.63-.14.26.1 1.64.78 1.92.92.28.14.46.21.53.32.07.12.07.7-.17 1.37z"/>
  </svg>
);

// Descarga el HCU-001 en PDF — compartido entre la lista y la ficha
// del paciente (antes solo vivía dentro de ListaPacientes).
const imprimirHCU = async (p) => {
  const url = `${(process.env.REACT_APP_API_URL || 'http://localhost:3001/api')}/reportes/hcu/${p.COD}`;
  const token = localStorage.getItem('token');
  try {
    const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await r.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `HCU_${p.APELLIDO_PATERNO}_${p.IDENTIFICACION}.pdf`;
    a.click();
  } catch (e) { alert('Error generando PDF: ' + e.message); }
};

/* --- CSS -------------------------------------------------- */
const css = `
.pac-header{background:#fff;color:var(--navy);padding:14px 18px;display:flex;align-items:center;justify-content:space-between;border-radius:10px 10px 0 0;border-bottom:1px solid var(--border);}
.pac-header-title{font-size:13px;font-weight:700;letter-spacing:.5px;text-transform:uppercase;color:var(--navy);}
.pac-toolbar{padding:14px 18px;background:#fff;border-bottom:1px solid var(--border);display:flex;align-items:center;gap:10px;flex-wrap:wrap;}
.pac-table thead tr{background:var(--blue-lt);color:var(--navy);}
.pac-table thead th{padding:13px 16px;font-size:10.5px;font-weight:700;letter-spacing:.5px;text-transform:uppercase;color:var(--navy);border:none;border-bottom:2px solid var(--border);white-space:nowrap;cursor:pointer;user-select:none;}
.pac-table thead th:hover{background:#e2e9f3;}
.pac-table thead th.sort-asc::after{content:' ↑';}
.pac-table thead th.sort-desc::after{content:' ↓';}
.pac-table tbody tr:hover{background:var(--blue-lt);}
.pac-table tbody td{padding:12px 16px;font-size:12.5px;border-bottom:1px solid var(--border);color:var(--text2);}
.pac-footer{padding:14px 18px;background:#fff;border-top:1px solid var(--border);display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:8px;border-radius:0 0 10px 10px;}
.pac-footer-info{font-size:11px;color:var(--text3);}
.pag{display:flex;align-items:center;gap:3px;}
.pag-btn{width:28px;height:28px;border-radius:5px;border:1px solid var(--border);background:#fff;font-size:11px;font-weight:600;color:var(--text2);cursor:pointer;display:flex;align-items:center;justify-content:center;transition:all .12s;}
.pag-btn:hover:not(:disabled){background:var(--bg);}
.pag-btn.active{background:var(--navy);color:#fff;border-color:var(--navy);}
.pag-btn:disabled{opacity:.35;cursor:default;}
.pag-ellipsis{font-size:11px;color:var(--text3);padding:0 2px;}
.act-icon{width:28px;height:28px;border-radius:5px;border:1px solid var(--border);background:#fff;display:inline-flex;align-items:center;justify-content:center;cursor:pointer;font-size:14px;transition:all .12s;color:var(--navy);}
.act-icon:hover{background:var(--blue-lt);}
.reg-tabs{display:flex;gap:8px;margin-bottom:0;border-bottom:2px solid var(--border);padding:0 20px;overflow-x:auto;}
.reg-tab{padding:10px 18px;font-size:12px;font-weight:600;color:var(--text3);cursor:pointer;border-bottom:2px solid transparent;margin-bottom:-2px;display:flex;align-items:center;gap:7px;transition:all .15s;white-space:nowrap;}
.reg-tab.active{color:var(--navy);border-bottom-color:var(--navy);}
.reg-tab:hover:not(.active){color:var(--text);}
.section-label{grid-column:1/-1;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.6px;color:var(--text3);padding:6px 0 4px;border-bottom:1px solid var(--bg);margin-top:4px;}
.pac-kpis{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin-bottom:16px;}
.pac-layout{display:flex;gap:16px;align-items:flex-start;}
.pac-side-panel{width:300px;flex-shrink:0;background:var(--surface);border-radius:10px;border:1px solid var(--border);overflow:hidden;}
@media (max-width: 900px){
  .pac-kpis{grid-template-columns:repeat(2,1fr);}
  .pac-layout{flex-direction:column;}
  .pac-side-panel{width:100%;}
}
@media (max-width: 520px){
  .pac-kpis{grid-template-columns:1fr;}
}

.table-wrap{overflow-x:auto;}

/* ── Tarjetas de paciente (celular) — ocultas por defecto ── */
.pac-cards-mobile{display:none;}
.pat-card{background:var(--surface);border:1px solid var(--border);border-radius:11px;padding:12px 13px;margin-bottom:9px;}
.pat-top{display:flex;gap:10px;align-items:flex-start;}
.pat-av{width:36px;height:36px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:700;flex-shrink:0;}
.pat-info{flex:1;min-width:0;}
.pat-name{font-size:12.5px;font-weight:600;color:var(--text);line-height:1.35;}
.pat-meta{display:flex;flex-wrap:wrap;gap:3px 8px;margin-top:4px;font-size:10.5px;color:var(--text3);}
.pat-acts{display:flex;gap:7px;margin-top:10px;justify-content:flex-end;border-top:1px solid var(--bg);padding-top:9px;}
.pat-acts button{width:32px;height:32px;border-radius:8px;border:1px solid var(--border);background:#fff;font-size:14px;color:var(--navy);cursor:pointer;display:flex;align-items:center;justify-content:center;}
.pat-acts button.on{background:var(--navy);color:#fff;border-color:var(--navy);}
.pat-acts button:hover{background:var(--blue-lt);}

@media (max-width: 680px){
  .pac-table-desktop{display:none;}
  .pac-cards-mobile{display:block;}
  .pac-header{flex-wrap:wrap;gap:8px;}
  .pac-toolbar{gap:8px;}
}

/* ── Tipo de paciente, edad detallada, alerta de duplicado,
     acciones rápidas (WhatsApp / Imprimir / Historia) ── */
.tipo-pac-box{background:#f1ecf9;border:1px solid #6b4e97;border-radius:8px;padding:10px 12px;}
.tipo-pac-box label{color:#6b4e97 !important;}
.edad-detalle{font-size:11px;color:var(--navy);font-weight:600;margin-top:3px;}
.dup-alert{font-size:11.5px;border-radius:8px;padding:8px 11px;margin-top:6px;background:#fceeec;color:#c0392b;display:flex;flex-wrap:wrap;gap:5px;align-items:center;}
.dup-alert a{color:#c0392b;text-decoration:underline;cursor:pointer;font-weight:600;}
.wa-quick-btn{white-space:nowrap;border:1px solid #2e7d4f;background:#e6f4ea;color:#2e7d4f;border-radius:6px;
  font-size:11px;font-weight:700;padding:0;width:36px;cursor:pointer;display:inline-flex;align-items:center;justify-content:center;gap:5px;}
.wa-quick-btn[disabled]{opacity:.4;cursor:default;}
.quick-round{width:32px;height:32px;border-radius:50%;border:none;color:#fff;font-size:14px;cursor:pointer;
  display:flex;align-items:center;justify-content:center;transition:.12s;}
.quick-round:hover{filter:brightness(1.08);}
.quick-round.blue{background:#2f6fed;}
.quick-round.pink{background:#c33e83;}

/* ── Secciones por acordeón (Ubicación / Datos adicionales /
     Emergencia / Facturación) — reemplazan las pestañas anteriores ── */
.acc{background:var(--surface);border:1px solid var(--border);border-radius:10px;margin-bottom:14px;overflow:hidden;}
.acc-head{padding:14px 20px;display:flex;align-items:center;justify-content:space-between;cursor:pointer;gap:12px;}
.acc-left{display:flex;align-items:center;gap:11px;}
.acc-ic{width:32px;height:32px;border-radius:8px;display:flex;align-items:center;justify-content:center;font-size:14px;flex-shrink:0;}
.acc-title{font-weight:700;font-size:13px;color:var(--text);}
.acc-sub{font-size:11px;color:var(--text3);margin-top:1px;}
.acc-chev{font-size:12px;color:var(--text3);transition:transform .15s;}
.acc.open .acc-chev{transform:rotate(180deg);}
.acc-inner{padding:4px 20px 20px;display:grid;grid-template-columns:1fr 1fr;gap:14px 16px;}
.acc-inner .full{grid-column:1/-1;}
.acc-block{padding:4px 20px 20px;}
.acc.verde .acc-ic{background:#e6f4ea;color:#2e7d4f;}
.acc.morado .acc-ic{background:#f1ecf9;color:#6b4e97;}
.acc.ambar .acc-ic{background:#fbf0df;color:#a86a1f;}
.acc.azul .acc-ic{background:#e8f0fe;color:#2f6fed;}
@media (max-width:820px){ .acc-inner{grid-template-columns:1fr;} }
`;

/* ===========================================
   LISTA DE PACIENTES
=========================================== */
function ListaPacientes({ onNuevo, onEditar, onHistoria, onImprimir }) {
  const [pacientes, setPacientes] = useState([]);
  const [total, setTotal]         = useState(0);
  const [loading, setLoading]     = useState(false);
  const [busqueda, setBusqueda]   = useState('');
  const [pageSize, setPageSize]   = useState(10);
  const [pagina, setPagina]       = useState(1);
  const [sortCol, setSortCol]     = useState('APELLIDO_PATERNO');
  const [sortDir, setSortDir]     = useState('ASC');
  const [verRapido, setVerRapido] = useState(null); // COD del paciente con vista rápida abierta
  const [resumenRapido, setResumenRapido] = useState(null);
  const [cargandoResumen, setCargandoResumen] = useState(false);
  const [kpis, setKpis] = useState(null);
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroSexo, setFiltroSexo] = useState('');
  const [filtroAlergias, setFiltroAlergias] = useState(false);
  const [filtroCumple, setFiltroCumple] = useState(false);
  const [filtroNuevos, setFiltroNuevos] = useState(false);
  const [exportando, setExportando] = useState(false);
  const busqRef = useRef('');
  const cargarKpis = useCallback(() => {
    api.get('/pacientes/kpis').then(({ data }) => setKpis(data)).catch((e) => console.error(e));
  }, []);
  useEffect(() => { cargarKpis(); }, [cargarKpis]);

  // Arma el query string de filtros — compartido entre cargar/contar/exportar
  // para que la tabla, el pie de página y el Excel siempre coincidan.
  const qsFiltros = useCallback(() => {
    const p = new URLSearchParams();
    if (filtroTipo) p.set('tipo', filtroTipo);
    if (filtroSexo) p.set('sexo', filtroSexo);
    if (filtroAlergias) p.set('alergias', '1');
    if (filtroCumple) p.set('cumpleanos', '1');
    if (filtroNuevos) p.set('nuevosMes', '1');
    return p.toString();
  }, [filtroTipo, filtroSexo, filtroAlergias, filtroCumple, filtroNuevos]);

  const cargar = useCallback(async (q, pg, ps, sc, sd) => {
    setLoading(true);
    try {
      const offset = (pg - 1) * ps;
      const extra = qsFiltros();
      const { data } = await api.get(
        `/pacientes?q=${encodeURIComponent(q)}&limit=${ps}&offset=${offset}&sort=${sc}&dir=${sd}${extra ? '&'+extra : ''}`
      );
      setPacientes(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [qsFiltros]);

  const contarTotal = useCallback(async (q) => {
    try {
      const extra = qsFiltros();
      const { data } = await api.get(`/pacientes/contar?q=${encodeURIComponent(q)}${extra ? '&'+extra : ''}`);
      setTotal(data.total);
    } catch { setTotal(0); }
  }, [qsFiltros]);

  useEffect(() => {
    busqRef.current = busqueda;
    const t = setTimeout(() => {
      if (busqRef.current !== busqueda) return;
      setPagina(1);
      contarTotal(busqueda);
      cargar(busqueda, 1, pageSize, sortCol, sortDir);
    }, 350);
    return () => clearTimeout(t);
  }, [busqueda, pageSize, sortCol, sortDir, filtroTipo, filtroSexo, filtroAlergias, filtroCumple, filtroNuevos]);

  useEffect(() => {
    cargar(busqueda, pagina, pageSize, sortCol, sortDir);
  }, [pagina]);

  // Los 4 KPI activan/limpian su filtro correspondiente en la tabla de abajo.
  const toggleFiltroKpi = (campo) => {
    // Los 3 filtros de KPI (alergias/cumpleaños/nuevos) son mutuamente excluyentes
    // entre sí, pero SÍ se pueden combinar con Tipo/Sexo — esos no se tocan aquí.
    if (campo === 'alergias')  { setFiltroAlergias(v => !v); setFiltroCumple(false); setFiltroNuevos(false); }
    if (campo === 'cumple')    { setFiltroCumple(v => !v);   setFiltroAlergias(false); setFiltroNuevos(false); }
    if (campo === 'nuevos')    { setFiltroNuevos(v => !v);   setFiltroAlergias(false); setFiltroCumple(false); }
    if (campo === 'activos')   { setFiltroAlergias(false); setFiltroCumple(false); setFiltroNuevos(false); setFiltroTipo(''); setFiltroSexo(''); setBusqueda(''); }
  };

  const exportarCSV = async () => {
    setExportando(true);
    try {
      const extra = qsFiltros();
      const { data } = await api.get(`/pacientes?q=${encodeURIComponent(busqueda)}&limit=100000&offset=0&sort=${sortCol}&dir=${sortDir}${extra ? '&'+extra : ''}`);
      const cab = ['Cédula','Apellidos','Nombres','Tipo','Fecha nacimiento','Sexo','Teléfono','Email'];
      const filas = data.map(p => [
        p.IDENTIFICACION||'', `${p.APELLIDO_PATERNO||''} ${p.APELLIDO_MATERNO||''}`.trim(),
        `${p.PRIMER_NOMBRE||''} ${p.SEGUNDO_NOMBRE||''}`.trim(), p.TIPO_PACIENTE||'PARTICULAR',
        p.FECHA_NACIMIENTO?p.FECHA_NACIMIENTO.substring(0,10):'', p.C_SEXO||'', p.TELEFONO||'', p.EMAIL||'',
      ]);
      const csv = '﻿'+[cab, ...filas].map(f => f.map(v => `"${String(v).replace(/"/g,'""')}"`).join(',')).join('\r\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `pacientes_${new Date().toISOString().slice(0,10)}.csv`;
      a.click();
    } catch (e) { alert('Error al exportar: ' + e.message); }
    setExportando(false);
  };

  const totalPags = Math.ceil(total / pageSize) || 1;

  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'ASC' ? 'DESC' : 'ASC');
    else { setSortCol(col); setSortDir('ASC'); }
    setPagina(1);
  };

  const abrirVistaRapida = async (p) => {
    if (verRapido === p.COD) { setVerRapido(null); setResumenRapido(null); return; }
    setVerRapido(p.COD); setResumenRapido(null); setCargandoResumen(true);
    try {
      const { data } = await api.get(`/pacientes/${p.COD}/resumen`);
      setResumenRapido(data);
    } catch (e) { console.error(e); }
    setCargandoResumen(false);
  };
  const thCls = (col) => sortCol === col ? `sort-${sortDir.toLowerCase()}` : '';

  const buildPages = () => {
    const pages = [];
    if (totalPags <= 7) {
      for (let i = 1; i <= totalPags; i++) pages.push(i);
    } else {
      pages.push(1);
      if (pagina > 3) pages.push('...');
      for (let i = Math.max(2, pagina-1); i <= Math.min(totalPags-1, pagina+1); i++) pages.push(i);
      if (pagina < totalPags - 2) pages.push('...');
      pages.push(totalPags);
    }
    return pages;
  };

  const desde = total === 0 ? 0 : (pagina-1)*pageSize + 1;
  const hasta  = Math.min(pagina*pageSize, total);

  return (
    <>
    <div className="pac-kpis">
      <div className="pac-kpi-card" style={{background:'var(--surface)',borderRadius:10,border:`1px solid ${!filtroAlergias&&!filtroCumple&&!filtroNuevos?'var(--navy)':'var(--border)'}`,padding:'14px 16px',cursor:'pointer'}}
        onClick={() => toggleFiltroKpi('activos')} title="Ver todos los pacientes activos">
        <div style={{fontSize:10.5,textTransform:'uppercase',fontWeight:700,color:'var(--text3)',letterSpacing:.4}}>Pacientes activos</div>
        <div style={{fontSize:24,fontWeight:800,color:'var(--navy)',margin:'4px 0 2px'}}>{kpis ? kpis.activos.toLocaleString() : '—'}</div>
        <div style={{fontSize:10.5,color:'#2e7d4f',fontWeight:700}}>total registrado</div>
      </div>
      <div className="pac-kpi-card" style={{background:'var(--surface)',borderRadius:10,border:`1px solid ${filtroNuevos?'var(--navy)':'var(--border)'}`,padding:'14px 16px',cursor:'pointer'}}
        onClick={() => toggleFiltroKpi('nuevos')} title="Filtrar pacientes nuevos de este mes">
        <div style={{fontSize:10.5,textTransform:'uppercase',fontWeight:700,color:'var(--text3)',letterSpacing:.4}}>Nuevos este mes</div>
        <div style={{fontSize:24,fontWeight:800,color:'var(--navy)',margin:'4px 0 2px'}}>{kpis ? kpis.nuevosMes.toLocaleString() : '—'}</div>
        <div style={{fontSize:10.5,color:'var(--navy)',fontWeight:700}}>{new Date().toLocaleDateString('es-EC',{month:'long',year:'numeric'})}</div>
      </div>
      <div className="pac-kpi-card" style={{background:'var(--surface)',borderRadius:10,border:`1px solid ${filtroAlergias?'var(--red)':'var(--border)'}`,padding:'14px 16px',cursor:'pointer'}}
        onClick={() => toggleFiltroKpi('alergias')} title="Filtrar pacientes con alergias registradas">
        <div style={{fontSize:10.5,textTransform:'uppercase',fontWeight:700,color:'var(--text3)',letterSpacing:.4}}>Con alergias registradas</div>
        <div style={{fontSize:24,fontWeight:800,color:'var(--navy)',margin:'4px 0 2px'}}>{kpis ? kpis.conAlergias.toLocaleString() : '—'}</div>
        <div style={{fontSize:10.5,color:'var(--red)',fontWeight:700}}>clic para filtrar la lista</div>
      </div>
      <div className="pac-kpi-card" style={{background:'var(--surface)',borderRadius:10,border:`1px solid ${filtroCumple?'#b8760f':'var(--border)'}`,padding:'14px 16px',cursor:'pointer'}}
        onClick={() => toggleFiltroKpi('cumple')} title="Filtrar cumpleaños de esta semana">
        <div style={{fontSize:10.5,textTransform:'uppercase',fontWeight:700,color:'var(--text3)',letterSpacing:.4}}>Cumpleaños esta semana</div>
        <div style={{fontSize:24,fontWeight:800,color:'var(--navy)',margin:'4px 0 2px'}}>{kpis ? kpis.cumpleSemana.toLocaleString() : '—'}</div>
        <div style={{fontSize:10.5,color:'#b8760f',fontWeight:700}}>clic para filtrar la lista</div>
      </div>
    </div>
    <div className="pac-layout">
    <div className="fade-up" style={{background:'var(--surface)',borderRadius:10,border:'1px solid var(--border)',overflow:'hidden',flex:1,minWidth:0}}>
      <div className="pac-header">
        <span className="pac-header-title">Pacientes</span>
        <div style={{display:'flex',gap:8}}>
          <button className="btn btn-sm" disabled={exportando} onClick={exportarCSV}>{exportando?'Exportando…':'⭳ Exportar'}</button>
          <button className="btn btn-sm" style={{background:'var(--navy)',color:'#fff',border:'none',fontWeight:700}}
            onClick={onNuevo}>+ Nuevo</button>
        </div>
      </div>

      <div className="pac-toolbar">
        <label style={{fontSize:12,color:'var(--text2)',display:'flex',alignItems:'center',gap:6}}>
          Mostrar
          <select className="form-control" style={{width:75,padding:'5px 8px'}}
            value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPagina(1); }}>
            {PAGE_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <div className="search-wrap" style={{flex:1,maxWidth:420,position:'relative'}}>
          <span className="search-icon" style={{fontSize:13}}>⊙</span>
          <input className="form-control" placeholder="Buscar por nombre, apellido o cédula..."
            value={busqueda} onChange={e => setBusqueda(e.target.value)} />
          {busqueda && (
            <span onClick={() => setBusqueda('')}
              style={{position:'absolute',right:10,top:'50%',transform:'translateY(-50%)',cursor:'pointer',color:'var(--text3)',fontSize:14}}>✕</span>
          )}
        </div>
        <select className="form-control" style={{width:150,padding:'6px 8px',fontSize:12}}
          value={filtroTipo} onChange={e => { setFiltroTipo(e.target.value); setPagina(1); }}>
          <option value="">Tipo: Todos</option>
          <option value="PARTICULAR">Particular</option>
          <option value="CONVENIO">Convenio</option>
          <option value="SEGURO">Seguro</option>
          <option value="PROGRAMA">Programa</option>
        </select>
        <select className="form-control" style={{width:130,padding:'6px 8px',fontSize:12}}
          value={filtroSexo} onChange={e => { setFiltroSexo(e.target.value); setPagina(1); }}>
          <option value="">Sexo: Todos</option>
          <option value="F">Femenino</option>
          <option value="M">Masculino</option>
        </select>
      </div>

      <div className="table-wrap pac-table-desktop">
        <table className="pac-table" style={{width:'100%',borderCollapse:'collapse'}}>
          <thead>
            <tr>
              <th className={thCls('IDENTIFICACION')} onClick={() => handleSort('IDENTIFICACION')} style={{width:130}}>Cédula</th>
              <th className={thCls('APELLIDO_PATERNO')} onClick={() => handleSort('APELLIDO_PATERNO')}>Paciente</th>
              <th className="hide-mobile" style={{width:100}}>Tipo</th>
              <th className={`hide-mobile ${thCls('FECHA_NACIMIENTO')}`} onClick={() => handleSort('FECHA_NACIMIENTO')} style={{width:110}}>Edad / Sexo</th>
              <th className="hide-mobile" style={{width:150}}>Teléfono</th>
              <th style={{width:120,textAlign:'center'}}>Acción</th>
            </tr>
          </thead>
          <tbody>
            {loading
              ? <tr><td colSpan={6} style={{textAlign:'center',padding:'32px 0'}}><div className="spinner" style={{margin:'0 auto'}} /></td></tr>
              : pacientes.length === 0
                ? <tr><td colSpan={6}><div className="empty-state">Sin resultados</div></td></tr>
                : pacientes.map(p => { const tb = TIPO_BADGE[p.TIPO_PACIENTE] || TIPO_BADGE.PARTICULAR; const wa = waLink(p.TELEFONO); return (
                  <tr key={p.COD}>
                    <td style={{fontWeight:600,color:'var(--navy)',fontFamily:'monospace',fontSize:12}}>{p.IDENTIFICACION||'—'}</td>
                    <td>
                      <div style={{display:'flex',alignItems:'center',gap:9}}>
                        <div style={{
                          width:30,height:30,borderRadius:'50%',flexShrink:0,
                          background: p.C_SEXO==='F' ? '#fbeaf5' : 'var(--blue-lt)',
                          color: p.C_SEXO==='F' ? '#993556' : 'var(--navy)',
                          display:'flex',alignItems:'center',justifyContent:'center',
                          fontSize:10,fontWeight:700
                        }}>
                          {p.PRIMER_NOMBRE?.[0]}{p.APELLIDO_PATERNO?.[0]}
                        </div>
                        <div>
                          <div style={{fontWeight:500,display:'flex',alignItems:'center',gap:5}}>
                            {p.NOMBRE_COMPLETO?.trim()}
                            {!!p.TIENE_ALERGIAS && <span title="Tiene alergias registradas" style={{fontSize:11}}>⚠️</span>}
                            {!!p.CUMPLE_SEMANA && <span title="Cumpleaños esta semana" style={{fontSize:11}}>🎂</span>}
                          </div>
                          <div style={{fontSize:10.5,color:'var(--text3)'}}>{p.C_SEXO==='F'?'registrada':'registrado'} {fmtFecha(p.FECHA_REGISTRO)}</div>
                        </div>
                      </div>
                    </td>
                    <td className="hide-mobile">
                      <span style={{background:tb.bg,color:tb.fg,fontSize:10.5,fontWeight:700,padding:'3px 9px',borderRadius:6,whiteSpace:'nowrap'}}>{tb.l}</span>
                    </td>
                    <td className="hide-mobile" style={{color:'var(--text2)',fontSize:12.5}}>{calcEdad(p.FECHA_NACIMIENTO).replace(' años','')} · {p.C_SEXO}</td>
                    <td className="hide-mobile" style={{color:'var(--text2)',fontSize:12}}>
                      <div style={{display:'flex',alignItems:'center',gap:6}}>
                        <span>{p.TELEFONO||'—'}</span>
                        {wa && <button type="button" className="wa-quick-btn" style={{width:26}} title="Abrir chat de WhatsApp"
                          onClick={() => window.open(wa, '_blank')}><WaIcon /></button>}
                      </div>
                    </td>
                    <td style={{textAlign:'center'}}>
                      <div style={{display:'flex',gap:4,justifyContent:'center'}}>
                        <div className="act-icon" title="Vista rápida" onClick={() => abrirVistaRapida(p)}
                          style={{background: verRapido===p.COD ? 'var(--navy)' : undefined, color: verRapido===p.COD ? '#fff' : undefined}}>👁</div>
                        <div className="act-icon" title="Editar" onClick={() => onEditar(p)}>✎</div>
                        <div className="act-icon" title="Historia clínica" onClick={() => onHistoria(p)}>⏱</div>
                        <div className="act-icon" title="Imprimir HCU-001" onClick={() => onImprimir(p)} style={{fontSize:13}}>⎙</div>
                      </div>
                    </td>
                  </tr>
                ); })}
          </tbody>
        </table>
      </div>

      {/* ── Vista celular: tarjetas en vez de tabla (evita el scroll
           horizontal que corta el boton "+ Nuevo" y el icono de Imprimir) ── */}
      <div className="pac-cards-mobile">
        {loading ? (
          <div className="spinner" style={{margin:'20px auto'}} />
        ) : pacientes.length === 0 ? (
          <div className="empty-state">Sin resultados</div>
        ) : pacientes.map(p => { const tb = TIPO_BADGE[p.TIPO_PACIENTE] || TIPO_BADGE.PARTICULAR; const wa = waLink(p.TELEFONO); return (
          <div key={p.COD} className="pat-card">
            <div className="pat-top">
              <div className="pat-av" style={{
                background: p.C_SEXO==='F' ? '#fbeaf5' : 'var(--blue-lt)',
                color: p.C_SEXO==='F' ? '#993556' : 'var(--navy)',
              }}>
                {p.PRIMER_NOMBRE?.[0]}{p.APELLIDO_PATERNO?.[0]}
              </div>
              <div className="pat-info">
                <div className="pat-name" style={{display:'flex',alignItems:'center',gap:5,flexWrap:'wrap'}}>
                  {p.NOMBRE_COMPLETO?.trim()}
                  {!!p.TIENE_ALERGIAS && <span title="Tiene alergias registradas" style={{fontSize:11}}>⚠️</span>}
                  {!!p.CUMPLE_SEMANA && <span title="Cumpleaños esta semana" style={{fontSize:11}}>🎂</span>}
                </div>
                <div className="pat-meta">
                  <span>🪪 {p.IDENTIFICACION || '—'}</span>
                  <span>· {p.C_SEXO==='F'?'registrada':'registrado'} {fmtFecha(p.FECHA_REGISTRO)}</span>
                  <span style={{background:tb.bg,color:tb.fg,fontWeight:700,padding:'1px 7px',borderRadius:6}}>{tb.l}</span>
                </div>
                {p.TELEFONO && (
                  <div className="pat-meta" style={{marginTop:3,alignItems:'center'}}>
                    <span>📞 {p.TELEFONO}</span>
                    {wa && <button type="button" className="wa-quick-btn" style={{width:24,height:22}} title="Abrir chat de WhatsApp"
                      onClick={() => window.open(wa, '_blank')}><WaIcon /></button>}
                  </div>
                )}
              </div>
            </div>
            <div className="pat-acts">
              <button className={verRapido===p.COD ? 'on' : ''} title="Vista rápida" onClick={() => abrirVistaRapida(p)}>👁</button>
              <button title="Editar" onClick={() => onEditar(p)}>✎</button>
              <button title="Historia clínica" onClick={() => onHistoria(p)}>⏱</button>
              <button title="Imprimir HCU-001" onClick={() => onImprimir(p)}>⎙</button>
            </div>
          </div>
        ); })}
      </div>

      <div className="pac-footer">
        <span className="pac-footer-info">
          {total > 0 ? `Mostrando ${desde} a ${hasta} de ${total.toLocaleString()} registros` : 'Sin registros'}
        </span>
        <div className="pag">
          <button className="pag-btn" disabled={pagina===1} onClick={() => setPagina(1)} title="Primera">⏮</button>
          <button className="pag-btn" disabled={pagina===1} onClick={() => setPagina(p=>p-1)} title="Anterior">‹</button>
          {buildPages().map((p,i) =>
            p === '...'
              ? <span key={`e${i}`} className="pag-ellipsis">…</span>
              : <button key={p} className={`pag-btn ${pagina===p?'active':''}`} onClick={() => setPagina(p)}>{p}</button>
          )}
          <button className="pag-btn" disabled={pagina===totalPags} onClick={() => setPagina(p=>p+1)} title="Siguiente">›</button>
          <button className="pag-btn" disabled={pagina===totalPags} onClick={() => setPagina(totalPags)} title="Última">⏭</button>
        </div>
      </div>
    </div>
        {verRapido && (
          <div className="pac-side-panel">
            <div style={{background:'var(--navy)',color:'#fff',padding:'16px 16px',display:'flex',alignItems:'flex-start',justifyContent:'space-between'}}>
              <div style={{display:'flex',alignItems:'center',gap:10}}>
                {resumenRapido && (
                  <div style={{
                    width:38,height:38,borderRadius:'50%',flexShrink:0,
                    background: resumenRapido.SEXO==='Femenino' ? '#fbeaf5' : 'var(--blue-lt)',
                    color: resumenRapido.SEXO==='Femenino' ? '#993556' : 'var(--navy)',
                    display:'flex',alignItems:'center',justifyContent:'center',
                    fontSize:13,fontWeight:700
                  }}>
                    {iniciales(resumenRapido.NOMBRE_COMPLETO)}
                  </div>
                )}
                <div>
                  <div style={{fontSize:14,fontWeight:700}}>{resumenRapido?.NOMBRE_COMPLETO || 'Cargando...'}</div>
                  {resumenRapido && <div style={{fontSize:11,color:'#c9d4e2',marginTop:2}}>HC {resumenRapido.HC} · {resumenRapido.EDAD} años · {resumenRapido.SEXO}</div>}
                </div>
              </div>
              <span onClick={() => { setVerRapido(null); setResumenRapido(null); }}
                style={{cursor:'pointer',fontSize:14,opacity:.8}}>✕</span>
            </div>
            <div style={{padding:'14px 16px'}}>
              {cargandoResumen ? (
                <div style={{textAlign:'center',padding:'24px 0',color:'var(--text3)',fontSize:12.5}}>Cargando...</div>
              ) : !resumenRapido ? (
                <div style={{textAlign:'center',padding:'24px 0',color:'var(--text3)',fontSize:12.5}}>No se pudo cargar</div>
              ) : (
                <>
                <div style={{marginBottom:14}}>
                  <div style={{fontSize:10,textTransform:'uppercase',fontWeight:700,color:'var(--text3)',marginBottom:6}}>Alergias</div>
                  {resumenRapido.alergias.length === 0
                    ? <span style={{fontSize:12,color:'var(--text3)'}}>Sin alergias registradas</span>
                    : resumenRapido.alergias.map((a,i) => (
                      <span key={i} style={{display:'inline-block',background:'#fdeaea',color:'var(--red)',fontSize:10.5,fontWeight:700,padding:'3px 9px',borderRadius:999,margin:'0 5px 5px 0'}}>⚠ {a}</span>
                    ))}
                </div>
                <div style={{marginBottom:14}}>
                  <div style={{fontSize:10,textTransform:'uppercase',fontWeight:700,color:'var(--text3)',marginBottom:6}}>Últimos signos vitales</div>
                  {resumenRapido.vitales ? (
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
                      <div style={{background:'#f8f9fb',borderRadius:8,padding:'8px 10px'}}>
                        <div style={{fontSize:9.5,color:'var(--text3)',fontWeight:700}}>PRESIÓN</div>
                        <div style={{fontSize:14,fontWeight:800,color:'var(--navy)'}}>{resumenRapido.vitales.PA || '—'}</div>
                      </div>
                      <div style={{background:'#f8f9fb',borderRadius:8,padding:'8px 10px'}}>
                        <div style={{fontSize:9.5,color:'var(--text3)',fontWeight:700}}>PULSO</div>
                        <div style={{fontSize:14,fontWeight:800,color:'var(--navy)'}}>{resumenRapido.vitales.PULSO || '—'}</div>
                      </div>
                      <div style={{background:'#f8f9fb',borderRadius:8,padding:'8px 10px'}}>
                        <div style={{fontSize:9.5,color:'var(--text3)',fontWeight:700}}>TEMP.</div>
                        <div style={{fontSize:14,fontWeight:800,color:'var(--navy)'}}>{resumenRapido.vitales.TEMP || '—'}</div>
                      </div>
                      <div style={{background:'#f8f9fb',borderRadius:8,padding:'8px 10px'}}>
                        <div style={{fontSize:9.5,color:'var(--text3)',fontWeight:700}}>SPO2</div>
                        <div style={{fontSize:14,fontWeight:800,color:'var(--navy)'}}>{resumenRapido.vitales.SAT_O2 || '—'}</div>
                      </div>
                    </div>
                  ) : <span style={{fontSize:12,color:'var(--text3)'}}>Sin registro reciente</span>}
                </div>
                <div style={{marginBottom:14}}>
                  <div style={{fontSize:10,textTransform:'uppercase',fontWeight:700,color:'var(--text3)',marginBottom:6}}>Historia reciente</div>
                  {resumenRapido.diagnosticosRecientes.length === 0
                    ? <span style={{fontSize:12,color:'var(--text3)'}}>Sin registros</span>
                    : resumenRapido.diagnosticosRecientes.map((d,i) => (
                      <div key={i} style={{borderLeft:'2px solid var(--border)',paddingLeft:10,marginBottom:8}}>
                        <div style={{fontSize:10,color:'var(--text3)'}}>{fmtFecha(d.FECHA)}</div>
                        <div style={{fontSize:12,color:'var(--navy)',fontWeight:600}}>{d.DESCRIPCION || d.CIE10}</div>
                      </div>
                    ))}
                </div>
                <div>
                  <div style={{fontSize:10,textTransform:'uppercase',fontWeight:700,color:'var(--text3)',marginBottom:6}}>Medicación activa</div>
                  {resumenRapido.medicacionActiva.length === 0
                    ? <span style={{fontSize:12,color:'var(--text3)'}}>Sin receta activa</span>
                    : resumenRapido.medicacionActiva.map((m,i) => (
                      <div key={i} style={{background:'#e6f4ea',color:'#2e7d4f',fontSize:11.5,fontWeight:700,padding:'7px 10px',borderRadius:8,marginBottom:6}}>💊 {m}</div>
                    ))}
                </div>
                </>
              )}
            </div>
          </div>
        )}
        </div>
    </>
  );
}

/* -- Campo reutilizable — FUERA de FormPaciente para que React
      no lo destruya/recree en cada render (bug de pérdida de foco) -- */
function CampoForm({ k, label, req, type='text', opts, placeholder, form, errors, set, onBlur }) {
  return (
    <div>
      <label className="form-label">{label}{req && <span style={{color:'var(--red)'}}> *</span>}</label>
      {opts
        ? <select className="form-control" style={errors[k]?{borderColor:'var(--red)'}:{}}
            value={form[k]||''} onChange={e => set(k, e.target.value)}>
            <option value="">Seleccione</option>
            {opts.map(o => <option key={o.v||o.COD} value={o.v||o.COD}>{o.l||o.DESCRIPCION}</option>)}
          </select>
        : <input className="form-control" type={type} placeholder={placeholder}
            style={errors[k]?{borderColor:'var(--red)'}:{}}
            value={form[k]||''} onChange={e => set(k, e.target.value)} onBlur={onBlur} />}
      {errors[k] && <div style={{fontSize:10,color:'var(--red)',marginTop:2}}>{errors[k]}</div>}
    </div>
  );
}

function FormPaciente({ paciente, onVolver, onGuardado, onVerFicha }) {
  const esNuevo = !paciente?.COD;
  const navigate = useNavigate();
  /* -- Secciones por acordeón (reemplaza las pestañas anteriores) -- */
  const [openAcc, setOpenAcc] = useState({ ubicacion:false, adicional:false, emergencia:true, facturacion:false });
  const toggleAcc = k => setOpenAcc(o => ({ ...o, [k]: !o[k] }));

  /* -- Estado: datos de filiación -- */
  const [form, setForm] = useState({
    C_TIPO_IDENTIFICACION: 'CDL',
    IDENTIFICACION: '',
    APELLIDO_PATERNO: '', APELLIDO_MATERNO: '',
    PRIMER_NOMBRE: '',    SEGUNDO_NOMBRE: '',
    FECHA_NACIMIENTO: '', C_SEXO: '',
    TIPO_PACIENTE: 'PARTICULAR',
    C_ESTADO_CIVIL: '',   C_INSTRUCCION: '',  C_OCUPACION: '',
    NACIONALIDAD: '',     AUTOIDENTIFICACION: '', TIPO_BONO: '',
    COD_PROVINCIA: '',    COD_CANTON: '',     COD_PARROQUIA: '',
    DIRECCION_DOMICILIO: '',
    CALLE_PRINCIPAL: '',  CALLE_SECUNDARIA: '', NUMERACION: '', REFERENCIA: '', BARRIO: '',
    TELEFONO: '',         EMAIL: '',
    EMPRESA_TRABAJO: '',  DIRECCION_TRABAJO: '', TELEFONO_TRABAJO: '',
    ...paciente,
  });

  /* -- Alerta de cédula duplicada (usa /pacientes/verificar-cedula ya
        existente) — se dispara al salir del campo de identificación -- */
  const [dupCheck, setDupCheck] = useState(null); // { existe, paciente } | null
  const verificarDuplicado = async () => {
    const ced = (form.IDENTIFICACION || '').trim();
    if (!ced) { setDupCheck(null); return; }
    try {
      const excluir = paciente?.COD ? `?excluir=${paciente.COD}` : '';
      const { data } = await api.get(`/pacientes/verificar-cedula/${encodeURIComponent(ced)}${excluir}`);
      setDupCheck(data.existe ? data : null);
    } catch { /* no bloquea el flujo si falla la verificación */ }
  };

  /* -- Estado: contactos de emergencia (personas_referentes) -- */
  const CONTACTO_VACIO = { NOMBRES:'', TELEFONO:'', EMAIL:'', DIRECCION:'', RELACION:'Cónyuge' };
  const [contactos, setContactos] = useState([{ ...CONTACTO_VACIO }]);

  /* -- Estado: facturación (clientes) -- */
  const FAC_VACIO = { C_TIPO_IDENTIFICACION:'CDL', IDENTIFICACION:'', APELLIDOS:'', NOMBRES:'', DIRECCION:'', TELEFONO:'', EMAIL:'' };
  const [facturacion, setFacturacion] = useState([{ ...FAC_VACIO }]);

  /* -- Catálogos -- */
  const [provincias,  setProvincias]  = useState([]);
  const [cantones,    setCantones]    = useState([]);
  const [parroquias,  setParroquias]  = useState([]);
  const [instruccion, setInstruccion] = useState([]);
  const [ocupacion,   setOcupacion]   = useState([]);
  const [estadoCivil, setEstadoCivil] = useState([]);

  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [msg,    setMsg]    = useState('');
  // FOTO_URL en BD: /uploads/fotos/paciente_123.jpg
  // Se sirve desde la raiz de la API (sin /api), ej: http://localhost:3001/uploads/fotos/...
  const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:3001/api').replace(/\/api$/, '');
  const [fotoPreview, setFotoPreview] = useState(paciente?.FOTO_URL ? `${API_BASE}${paciente.FOTO_URL}` : null);
  const [fotoFile,    setFotoFile]    = useState(null);
  const fotoRef = useRef(null);

  /* -- Autocompletar por cédula -- */
  const [buscandoCedula, setBuscandoCedula] = useState(false);
  const [msgCedula, setMsgCedula] = useState('');
  const buscarPorCedula = async () => {
    const cedula = (form.IDENTIFICACION || '').trim();
    if (!/^\d{10}$/.test(cedula)) { setMsgCedula('Ingresa una cédula válida de 10 dígitos'); return; }
    setBuscandoCedula(true); setMsgCedula('');
    try {
      const { data } = await api.get(`/pacientes/consultar-cedula/${cedula}`);
      setForm(f => ({
        ...f,
        APELLIDO_PATERNO: data.APELLIDO_PATERNO || f.APELLIDO_PATERNO,
        APELLIDO_MATERNO: data.APELLIDO_MATERNO || f.APELLIDO_MATERNO,
        PRIMER_NOMBRE:    data.PRIMER_NOMBRE    || f.PRIMER_NOMBRE,
        SEGUNDO_NOMBRE:   data.SEGUNDO_NOMBRE   || f.SEGUNDO_NOMBRE,
        FECHA_NACIMIENTO: data.FECHA_NACIMIENTO || f.FECHA_NACIMIENTO,
        C_SEXO:           data.C_SEXO           || f.C_SEXO,
        DIRECCION_DOMICILIO: data.DIRECCION_DOMICILIO || f.DIRECCION_DOMICILIO,
      }));
      setMsgCedula(data.DIRECCION_DOMICILIO ? '✓ Datos completados (incluye dirección)' : '✓ Datos completados');
    } catch (e) {
      setMsgCedula(e.response?.data?.error || 'No se pudo consultar la cédula');
    }
    setBuscandoCedula(false);
  };
  /* -- Autocompletar por cédula (Datos de facturación) -- */
  const [buscandoCedulaFac, setBuscandoCedulaFac] = useState(null);
  const [msgCedulaFac, setMsgCedulaFac] = useState({});
  const buscarPorCedulaFac = async (i) => {
    const cedula = (facturacion[i]?.IDENTIFICACION || '').trim();
    if (!/^\d{10}$/.test(cedula)) { setMsgCedulaFac(m => ({...m,[i]:'Ingresa una cédula válida de 10 dígitos'})); return; }
    setBuscandoCedulaFac(i); setMsgCedulaFac(m => ({...m,[i]:''}));
    try {
      const { data } = await api.get(`/pacientes/consultar-cedula/${cedula}`);
      setFac(i, 'APELLIDOS', `${data.APELLIDO_PATERNO||''} ${data.APELLIDO_MATERNO||''}`.trim());
      setFac(i, 'NOMBRES', `${data.PRIMER_NOMBRE||''} ${data.SEGUNDO_NOMBRE||''}`.trim());
      setMsgCedulaFac(m => ({...m,[i]:'✓ Datos completados'}));
    } catch (e) {
      setMsgCedulaFac(m => ({...m,[i]: e.response?.data?.error || 'No se pudo consultar la cédula'}));
    }
    setBuscandoCedulaFac(null);
  };

  /* -- Carga inicial -- */
  useEffect(() => {
    api.get('/catalogos/provincias').then(r => setProvincias(r.data)).catch(()=>{});
    api.get('/catalogos/instruccion').then(r => setInstruccion(r.data)).catch(()=>{});
    api.get('/catalogos/ocupacion').then(r => setOcupacion(r.data)).catch(()=>{});
    api.get('/catalogos/estado-civil').then(r => setEstadoCivil(r.data)).catch(()=>{});

    if (!esNuevo && paciente?.COD) {
      // Cargar contactos y facturación existentes
      api.get(`/pacientes/${paciente.COD}/contactos`)
        .then(r => { if (r.data.length) setContactos(r.data); })
        .catch(()=>{});
      api.get(`/pacientes/${paciente.COD}/facturacion`)
        .then(r => { if (r.data) setFacturacion([r.data]); })
        .catch(()=>{});
      // Cargar cadena provincia->cantón para la parroquia guardada
      if (paciente.COD_PROVINCIA) {
        api.get(`/catalogos/cantones/${paciente.COD_PROVINCIA}`).then(r => setCantones(r.data)).catch(()=>{});
      }
      if (paciente.COD_CANTON) {
        api.get(`/catalogos/parroquias/${paciente.COD_CANTON}`).then(r => setParroquias(r.data)).catch(()=>{});
      }
    }
  }, []);

  /* -- Helpers filiación -- */
  const set = (k, v) => { setForm(f => ({...f, [k]:v})); setErrors(e => ({...e, [k]:''})); };

  const onProvincia = async (cod) => {
    set('COD_PROVINCIA', cod); set('COD_CANTON',''); set('COD_PARROQUIA','');
    setCantones([]); setParroquias([]);
    if (cod) { const { data } = await api.get(`/catalogos/cantones/${cod}`); setCantones(data); }
  };
  const onCanton = async (cod) => {
    set('COD_CANTON', cod); set('COD_PARROQUIA','');
    setParroquias([]);
    if (cod) { const { data } = await api.get(`/catalogos/parroquias/${cod}`); setParroquias(data); }
  };

  /* -- Helpers contactos -- */
  const setContacto = (i, k, v) => setContactos(cs => cs.map((c,idx) => idx===i ? {...c,[k]:v} : c));
  const addContacto    = () => setContactos(cs => [...cs, {...CONTACTO_VACIO}]);
  const removeContacto = (i) => setContactos(cs => cs.filter((_,idx) => idx!==i));

  /* -- Helpers facturación -- */
  const setFac = (i, k, v) => setFacturacion(fs => fs.map((f,idx) => idx===i ? {...f,[k]:v} : f));
  const addFac    = () => setFacturacion(fs => [...fs, {...FAC_VACIO}]);
  const removeFac = (i) => setFacturacion(fs => fs.filter((_,idx) => idx!==i));

  /* -- Validación -- */
  const validar = () => {
    const e = {};
    if (!form.APELLIDO_PATERNO) e.APELLIDO_PATERNO = 'Requerido';
    if (!form.PRIMER_NOMBRE)    e.PRIMER_NOMBRE    = 'Requerido';
    if (!form.C_SEXO)           e.C_SEXO           = 'Requerido';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  /* -- Guardar todo -- */
  const guardar = async () => {
    if (!validar()) { return; }
    setSaving(true); setMsg('');
    try {
      const { COD_PROVINCIA, COD_CANTON, ...payload } = form;

      // 1. Paciente principal
      let codPaciente = paciente?.COD;
      if (esNuevo) {
        const { data } = await api.post('/pacientes', payload);
        codPaciente = data.COD;
      } else {
        await api.put(`/pacientes/${codPaciente}`, payload);
      }

      // 2. Contactos de emergencia — solo los que tienen nombre
      const contactosValidos = contactos.filter(c => c.NOMBRES?.trim());
      await api.post(`/pacientes/${codPaciente}/contactos`, contactosValidos);

      // 3. Facturación — solo el primero si tiene identificación
      const facValida = facturacion[0];
      if (facValida?.IDENTIFICACION?.trim() && facValida?.APELLIDOS?.trim() && facValida?.NOMBRES?.trim()) {
        await api.post(`/pacientes/${codPaciente}/facturacion`, facValida);
      }

      // 4. Foto — si el usuario seleccionó una nueva imagen
      if (fotoFile) {
        const formData = new FormData();
        formData.append('foto', fotoFile);
        await api.post(`/pacientes/${codPaciente}/foto`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      setMsg('✓ Guardado correctamente');
      setTimeout(() => onGuardado(), 1000);
    } catch (e) {
      setMsg('Error: ' + (e.response?.data?.error || e.message));
    } finally { setSaving(false); }
  };

  /* -- Alias corto para no reescribir todo el JSX -- */
  // F ya está definido fuera del componente — pasamos las props de contexto via bind
  // Fc eliminado — usar CampoForm directamente con props

  /* -- Estilo tabla grilla -- */
  const thStyle = { padding:'9px 10px', fontSize:11, fontWeight:600, color:'#fff',
                    background:'#1a2535', borderRight:'1px solid #2e3d52', whiteSpace:'nowrap' };
  const tdStyle = { padding:'5px 6px', borderBottom:'1px solid var(--border)', verticalAlign:'middle' };
  const inpStyle = { width:'100%', padding:'5px 8px', fontSize:12, border:'1px solid var(--border)',
                     borderRadius:5, background:'#fff', color:'var(--text)' };
  const selStyle = { ...inpStyle };
  const btnAdd = { width:26, height:26, borderRadius:5, border:'none', background:'#0d6efd',
                   color:'#fff', fontSize:16, cursor:'pointer', display:'flex',
                   alignItems:'center', justifyContent:'center', flexShrink:0 };
  const btnDel = { width:26, height:26, borderRadius:5, border:'none', background:'#dc3545',
                   color:'#fff', fontSize:14, cursor:'pointer', display:'flex',
                   alignItems:'center', justifyContent:'center', flexShrink:0 };

  const RELACIONES = ['Cónyuge','Padre','Madre','Hijo/a','Hermano/a','Abuelo/a','Tío/a','Amigo/a','Otro'];
  const TIPOS_ID   = [{v:'CDL',l:'Cédula'},{v:'PSP',l:'Pasaporte'},{v:'RUC',l:'RUC'},{v:'NAP',l:'No aplica'}];
  const TIPOS_PACIENTE = [
    {v:'PARTICULAR', l:'Particular'},
    {v:'CONVENIO',   l:'Convenio corporativo'},
    {v:'SEGURO',     l:'Seguro privado'},
    {v:'PROGRAMA',   l:'Programa de control de peso'},
  ];
  const waHref = waLink(form.TELEFONO);

  return (
    <>
    <div className="fade-up" style={{background:'var(--surface)',borderRadius:10,border:'1px solid var(--border)',marginBottom:16}}>

      {/* Encabezado */}
      <div style={{padding:'12px 20px',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
        <div style={{display:'flex',alignItems:'center',gap:10}}>
          <button className="btn btn-sm" onClick={onVolver}>← Atrás</button>
          <span style={{fontSize:14,fontWeight:700,color:'var(--text)'}}>
            {esNuevo ? 'Nuevo paciente' : 'Editar paciente'}
          </span>
          {!esNuevo && (
            <div style={{display:'flex',gap:6,marginLeft:4}}>
              <button className="quick-round blue" title="Imprimir HCU-001" onClick={() => imprimirHCU(paciente)}>🖨</button>
              <button className="quick-round pink" title="Historia clínica" onClick={() => navigate(`/historia?paciente=${paciente.COD}`)}>🗂</button>
            </div>
          )}
        </div>
        <div style={{display:'flex',gap:8,alignItems:'center'}}>
          {msg && <span style={{fontSize:12,color:msg.startsWith('✓')?'var(--green)':'var(--red)',fontWeight:500}}>{msg}</span>}
          <button className="btn btn-primary btn-sm" disabled={saving} onClick={guardar}>
            {saving ? 'Guardando...' : '✓ Guardar'}
          </button>
        </div>
      </div>

      {/* Datos de filiación — siempre visibles (antes era la pestaña 0) */}
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'14px 20px',padding:20}}>

          {/* Zona de foto — ocupa columna derecha fila 1 a 4 */}
          <div style={{gridColumn:2,gridRow:'1/5',display:'flex',flexDirection:'column',alignItems:'center',gap:10,paddingTop:4}}>
            <div
              onClick={() => fotoRef.current?.click()}
              style={{
                width:130,height:130,borderRadius:'50%',border:'2px dashed var(--border)',
                display:'flex',alignItems:'center',justifyContent:'center',
                cursor:'pointer',overflow:'hidden',background:'var(--surface2)',
                transition:'border-color .15s',flexShrink:0,
              }}
              onMouseEnter={e => e.currentTarget.style.borderColor='var(--navy)'}
              onMouseLeave={e => e.currentTarget.style.borderColor='var(--border)'}
            >
              {fotoPreview
                ? <img src={fotoPreview} alt="Foto paciente"
                    style={{width:'100%',height:'100%',objectFit:'cover'}} />
                : <div style={{textAlign:'center',padding:10}}>
                    <div style={{fontSize:44,opacity:.2}}>👤</div>
                    <div style={{fontSize:10,color:'var(--text3)',marginTop:4}}>Subir foto</div>
                  </div>
              }
            </div>
            <input ref={fotoRef} type="file" accept="image/jpeg,image/png,image/webp"
              style={{display:'none'}}
              onChange={e => {
                const file = e.target.files[0];
                if (!file) return;
                if (file.size > 3 * 1024 * 1024) { alert('La imagen no puede superar 3 MB'); return; }
                setFotoFile(file);
                setFotoPreview(URL.createObjectURL(file));
              }} />
            {fotoPreview && (
              <button className="btn btn-sm"
                style={{color:'var(--red)',borderColor:'var(--red)',background:'transparent',fontSize:11}}
                onClick={async () => {
                  setFotoPreview(null); setFotoFile(null);
                  if (!esNuevo && paciente?.COD) {
                    try { await api.delete(`/pacientes/${paciente.COD}/foto`); } catch {}
                  }
                }}>
                Eliminar imagen
              </button>
            )}

          </div>

          <div className="section-label" style={{gridColumn:1}}>Identificación</div>
          <CampoForm form={form} errors={errors} set={set} k="C_TIPO_IDENTIFICACION" label="Tipo identificación" opts={TIPOS_ID} />
          <div>
            <CampoForm form={form} errors={errors} set={set} k="IDENTIFICACION" label="Número de identificación" placeholder="Ej: 1712345678" onBlur={verificarDuplicado} />
            {form.C_TIPO_IDENTIFICACION === 'CDL' && (
              <>
                <button type="button" className="btn btn-sm" style={{marginTop:4}}
                  disabled={buscandoCedula || !/^\d{10}$/.test(form.IDENTIFICACION||'')}
                  onClick={buscarPorCedula}>
                  {buscandoCedula ? 'Buscando...' : '🔍 Autocompletar con cédula'}
                </button>
                {msgCedula && <div style={{fontSize:11,marginTop:3,color:msgCedula.startsWith('✓')?'var(--green)':'var(--red)'}}>{msgCedula}</div>}
              </>
            )}
            {dupCheck?.existe && (
              <div className="dup-alert">
                ⚠ Ya existe un paciente con esta cédula: <b>{dupCheck.paciente.NOMBRE_COMPLETO}</b>
                {onVerFicha && <a onClick={() => onVerFicha(dupCheck.paciente.COD)}>ver ficha</a>}
              </div>
            )}
          </div>

          <div className="tipo-pac-box" style={{gridColumn:1}}>
            <CampoForm form={form} errors={errors} set={set} k="TIPO_PACIENTE" label="Tipo de paciente" opts={TIPOS_PACIENTE} />
          </div>

          <div className="section-label">Nombres y apellidos</div>
          <div>
            <label className="form-label">Apellidos <span style={{color:'var(--red)'}}>*</span></label>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
              <div>
                <input className="form-control" placeholder="Paterno"
                  style={errors.APELLIDO_PATERNO?{borderColor:'var(--red)'}:{}}
                  value={form.APELLIDO_PATERNO||''} onChange={e => set('APELLIDO_PATERNO',e.target.value)} />
                {errors.APELLIDO_PATERNO && <div style={{fontSize:10,color:'var(--red)',marginTop:2}}>{errors.APELLIDO_PATERNO}</div>}
              </div>
              <input className="form-control" placeholder="Materno"
                value={form.APELLIDO_MATERNO||''} onChange={e => set('APELLIDO_MATERNO',e.target.value)} />
            </div>
          </div>
          <div>
            <label className="form-label">Nombres <span style={{color:'var(--red)'}}>*</span></label>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
              <div>
                <input className="form-control" placeholder="Primer nombre"
                  style={errors.PRIMER_NOMBRE?{borderColor:'var(--red)'}:{}}
                  value={form.PRIMER_NOMBRE||''} onChange={e => set('PRIMER_NOMBRE',e.target.value)} />
                {errors.PRIMER_NOMBRE && <div style={{fontSize:10,color:'var(--red)',marginTop:2}}>{errors.PRIMER_NOMBRE}</div>}
              </div>
              <input className="form-control" placeholder="Segundo nombre"
                value={form.SEGUNDO_NOMBRE||''} onChange={e => set('SEGUNDO_NOMBRE',e.target.value)} />
            </div>
          </div>

          <div className="section-label">Datos demográficos</div>
          <div>
            <label className="form-label">Fecha de nacimiento</label>
            <input type="date" className="form-control"
              value={form.FECHA_NACIMIENTO ? form.FECHA_NACIMIENTO.substring(0,10) : ''}
              onChange={e => set('FECHA_NACIMIENTO',e.target.value)} />
            {form.FECHA_NACIMIENTO && (
              <div className="edad-detalle">{fmtEdadDetallada(form.FECHA_NACIMIENTO)}</div>
            )}
          </div>
          <CampoForm form={form} errors={errors} set={set} k="C_SEXO" label="Sexo" req opts={[{v:'M',l:'Masculino'},{v:'F',l:'Femenino'}]} />

          <div className="section-label">Contacto</div>
          <div>
            <label className="form-label">Teléfono principal</label>
            <div style={{display:'flex',gap:6}}>
              <input className="form-control" placeholder="Principal"
                value={form.TELEFONO||''} onChange={e => set('TELEFONO',e.target.value)} />
              <button type="button" className="wa-quick-btn" disabled={!waHref}
                title={waHref ? 'Abrir chat de WhatsApp' : 'Ingresa un celular ecuatoriano válido (09XXXXXXXX)'}
                onClick={() => window.open(waHref, '_blank')}><WaIcon /></button>
            </div>
          </div>
          <CampoForm form={form} errors={errors} set={set} k="EMAIL" label="Correo electrónico" type="email" />
          <div style={{gridColumn:'1/-1'}}>
            <label className="form-label">Dirección de domicilio</label>
            <input className="form-control" placeholder="Descripción general"
              value={form.DIRECCION_DOMICILIO||''} onChange={e => set('DIRECCION_DOMICILIO',e.target.value)} />
          </div>
        </div>
    </div>

    {/* ================= Ubicación y demografía (verde) ================= */}
    <div className={`acc verde ${openAcc.ubicacion ? 'open' : ''}`}>
      <div className="acc-head" onClick={() => toggleAcc('ubicacion')}>
        <div className="acc-left">
          <div className="acc-ic">📍</div>
          <div>
            <div className="acc-title">Ubicación y demografía</div>
            <div className="acc-sub">Provincia, cantón, dirección detallada, nacionalidad y más</div>
          </div>
        </div>
        <div className="acc-chev">{openAcc.ubicacion ? '▴' : '▾'}</div>
      </div>
      {openAcc.ubicacion && (
        <div className="acc-inner">
          <CampoForm form={form} errors={errors} set={set} k="NACIONALIDAD" label="Nacionalidad" opts={[
            {v:'ECU',l:'Ecuatoriana'},{v:'COL',l:'Colombiana'},{v:'PER',l:'Peruana'},
            {v:'VEN',l:'Venezolana'},{v:'USA',l:'Estadounidense'},{v:'OTR',l:'Otra'},
          ]} />
          <CampoForm form={form} errors={errors} set={set} k="AUTOIDENTIFICACION" label="Autoidentificacion etnica" opts={[
            {v:'MES',l:'Mestizo/a'},{v:'IND',l:'Indigena'},{v:'AFR',l:'Afroecuatoriano/a'},
            {v:'MON',l:'Montubio/a'},{v:'BLA',l:'Blanco/a'},{v:'OTR',l:'Otro'},
          ]} />
          <CampoForm form={form} errors={errors} set={set} k="C_ESTADO_CIVIL" label="Estado civil" opts={estadoCivil.length ? estadoCivil : [
            {v:'SLT',l:'Soltero/a'},{v:'CSD',l:'Casado/a'},{v:'DVR',l:'Divorciado/a'},
            {v:'VDO',l:'Viudo/a'},{v:'UNL',l:'Unión libre'},
          ]} />
          <CampoForm form={form} errors={errors} set={set} k="C_INSTRUCCION" label="Escolaridad" opts={instruccion.length ? instruccion : [
            {v:'INF',l:'Infante'},{v:'NNG',l:'Ninguna'},
            {v:'PRI',l:'Primaria incompleta'},{v:'PRC',l:'Primaria completa'},
            {v:'SCI',l:'Secundaria incompleta'},{v:'SCC',l:'Secundaria completa'},
            {v:'SPI',l:'Superior incompleta'},{v:'SPC',l:'Superior completa'},
          ]} />
          <CampoForm form={form} errors={errors} set={set} k="TIPO_BONO" label="Tipo de bono" opts={[
            {v:'BDH',l:'Bono de Desarrollo Humano'},{v:'PEN',l:'Pension asistencial'},
            {v:'JUB',l:'Jubilacion'},{v:'NIN',l:'Ninguno'},
          ]} />
          <div>
            <label className="form-label">Provincia</label>
            <select className="form-control" value={form.COD_PROVINCIA||''} onChange={e => onProvincia(e.target.value)}>
              <option value="">Seleccione</option>
              {provincias.map(p => <option key={p.COD} value={p.COD}>{p.PROVINCIA}</option>)}
            </select>
          </div>
          <div>
            <label className="form-label">Cantón</label>
            <select className="form-control" value={form.COD_CANTON||''} onChange={e => onCanton(e.target.value)} disabled={!cantones.length}>
              <option value="">Seleccione</option>
              {cantones.map(c => <option key={c.COD} value={c.COD}>{c.CANTON}</option>)}
            </select>
          </div>
          <div>
            <label className="form-label">Parroquia</label>
            <select className="form-control" value={form.COD_PARROQUIA||''} onChange={e => set('COD_PARROQUIA',e.target.value)} disabled={!parroquias.length}>
              <option value="">Seleccione</option>
              {parroquias.map(p => <option key={p.COD} value={p.COD}>{p.PARROQUIA}</option>)}
            </select>
          </div>
          <div>
            <label className="form-label">Calle principal</label>
            <input className="form-control" value={form.CALLE_PRINCIPAL||''} onChange={e => set('CALLE_PRINCIPAL',e.target.value)} />
          </div>
          <div>
            <label className="form-label">Numeracion</label>
            <input className="form-control" value={form.NUMERACION||''} onChange={e => set('NUMERACION',e.target.value)} />
          </div>
          <div>
            <label className="form-label">Calle secundaria</label>
            <input className="form-control" value={form.CALLE_SECUNDARIA||''} onChange={e => set('CALLE_SECUNDARIA',e.target.value)} />
          </div>
          <div>
            <label className="form-label">Referencia</label>
            <input className="form-control" value={form.REFERENCIA||''} onChange={e => set('REFERENCIA',e.target.value)} />
          </div>
          <div>
            <label className="form-label">Barrio / Sector</label>
            <input className="form-control" value={form.BARRIO||''} onChange={e => set('BARRIO',e.target.value)} />
          </div>
          {!esNuevo && paciente?.PARROQUIA && (
            <div className="full" style={{background:'var(--blue-lt)',borderRadius:8,padding:'8px 12px',fontSize:12,color:'var(--navy)'}}>
              Ubicación actual: <strong>{paciente.PARROQUIA}</strong>
              {paciente.CANTON && <> · {paciente.CANTON}</>}
              {paciente.PROVINCIA && <> · {paciente.PROVINCIA}</>}
            </div>
          )}
        </div>
      )}
    </div>

    {/* ================= Datos adicionales (morado) ================= */}
    <div className={`acc morado ${openAcc.adicional ? 'open' : ''}`}>
      <div className="acc-head" onClick={() => toggleAcc('adicional')}>
        <div className="acc-left">
          <div className="acc-ic">💼</div>
          <div>
            <div className="acc-title">Datos adicionales</div>
            <div className="acc-sub">Ocupación y datos laborales</div>
          </div>
        </div>
        <div className="acc-chev">{openAcc.adicional ? '▴' : '▾'}</div>
      </div>
      {openAcc.adicional && (
        <div className="acc-inner">
          <CampoForm form={form} errors={errors} set={set} k="C_OCUPACION" label="Ocupacion" opts={ocupacion.length ? ocupacion : []} />
          <CampoForm form={form} errors={errors} set={set} k="EMPRESA_TRABAJO"  label="Empresa donde trabaja" />
          <div>
            <label className="form-label">Teléfono de trabajo</label>
            <input className="form-control" value={form.TELEFONO_TRABAJO||''} onChange={e => set('TELEFONO_TRABAJO',e.target.value)} />
          </div>
          <div className="full">
            <label className="form-label">Dirección del trabajo</label>
            <input className="form-control"
              value={form.DIRECCION_TRABAJO||''} onChange={e => set('DIRECCION_TRABAJO',e.target.value)} />
          </div>
        </div>
      )}
    </div>

    {/* ================= Contacto de emergencia (ámbar) ================= */}
    <div className={`acc ambar ${openAcc.emergencia ? 'open' : ''}`}>
      <div className="acc-head" onClick={() => toggleAcc('emergencia')}>
        <div className="acc-left">
          <div className="acc-ic">☎</div>
          <div>
            <div className="acc-title">Contacto de emergencia</div>
            <div className="acc-sub">Se puede registrar más de un contacto</div>
          </div>
        </div>
        <div className="acc-chev">{openAcc.emergencia ? '▴' : '▾'}</div>
      </div>
      {openAcc.emergencia && (
        <div className="acc-block">
          <div style={{overflowX:'auto'}}>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
              <thead>
                <tr>
                  <th style={{...thStyle,width:32}}>
                    <button style={btnAdd} title="Agregar contacto" onClick={addContacto}>+</button>
                  </th>
                  <th style={thStyle}>Nombre Completo <span style={{color:'#f87'}}>*</span></th>
                  <th style={thStyle}>Teléfono</th>
                  <th style={thStyle}>Parentesco</th>
                  <th style={thStyle}>Dirección</th>
                  <th style={thStyle}>Correo Electrónico</th>
                </tr>
              </thead>
              <tbody>
                {contactos.map((c,i) => (
                  <tr key={i}>
                    <td style={{...tdStyle,textAlign:'center'}}>
                      <button style={btnDel} onClick={() => removeContacto(i)}>✕</button>
                    </td>
                    <td style={tdStyle}>
                      <input style={inpStyle} value={c.NOMBRES||''} placeholder="Nombre completo"
                        onChange={e => setContacto(i,'NOMBRES',e.target.value)} />
                    </td>
                    <td style={tdStyle}>
                      <input style={inpStyle} value={c.TELEFONO||''} placeholder="Teléfono"
                        onChange={e => setContacto(i,'TELEFONO',e.target.value)} />
                    </td>
                    <td style={tdStyle}>
                      <select style={selStyle} value={c.RELACION||''} onChange={e => setContacto(i,'RELACION',e.target.value)}>
                        <option value="">Seleccione</option>
                        {RELACIONES.map(r => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </td>
                    <td style={tdStyle}>
                      <input style={inpStyle} value={c.DIRECCION||''} placeholder="Dirección"
                        onChange={e => setContacto(i,'DIRECCION',e.target.value)} />
                    </td>
                    <td style={tdStyle}>
                      <input style={inpStyle} type="email" value={c.EMAIL||''} placeholder="Email"
                        onChange={e => setContacto(i,'EMAIL',e.target.value)} />
                    </td>
                  </tr>
                ))}
                {contactos.length === 0 && (
                  <tr><td colSpan={6} style={{textAlign:'center',padding:'24px 0',color:'var(--text3)',fontSize:12}}>
                    Sin contactos — presione + para agregar
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>

    {/* ================= Datos de facturación (azul) ================= */}
    <div className={`acc azul ${openAcc.facturacion ? 'open' : ''}`}>
      <div className="acc-head" onClick={() => toggleAcc('facturacion')}>
        <div className="acc-left">
          <div className="acc-ic">🧾</div>
          <div>
            <div className="acc-title">Datos de facturación</div>
            <div className="acc-sub">Receptor(es) para la factura del paciente</div>
          </div>
        </div>
        <div className="acc-chev">{openAcc.facturacion ? '▴' : '▾'}</div>
      </div>
      {openAcc.facturacion && (
        <div className="acc-block">
          <div style={{overflowX:'auto'}}>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
              <thead>
                <tr>
                  <th style={{...thStyle,width:32}}>
                    <button style={btnAdd} title="Agregar receptor" onClick={addFac}>+</button>
                  </th>
                  <th style={{...thStyle,width:50}}>Copiar</th>
                  <th style={thStyle}>Tipo identificación <span style={{color:'#f87'}}>*</span></th>
                  <th style={thStyle}>Razón social — Cédula/RUC <span style={{color:'#f87'}}>*</span></th>
                  <th style={thStyle}>Teléfono <span style={{color:'#f87'}}>*</span></th>
                  <th style={thStyle}>Dirección <span style={{color:'#f87'}}>*</span></th>
                  <th style={thStyle}>Correo electrónico <span style={{color:'#f87'}}>*</span></th>
                </tr>
              </thead>
              <tbody>
                {facturacion.map((f,i) => (
                  <tr key={i}>
                    <td style={{...tdStyle,textAlign:'center'}}>
                      <button style={btnDel} onClick={() => removeFac(i)}>✕</button>
                    </td>
                    <td style={{...tdStyle,textAlign:'center'}}>
                      {/* Copiar datos del paciente */}
                      <button title="Copiar datos del paciente"
                        style={{...btnAdd,background:'var(--navy)',fontSize:11,width:50,borderRadius:5}}
                        onClick={() => setFac(i, 'IDENTIFICACION', form.IDENTIFICACION || '') ||
                          setFac(i,'C_TIPO_IDENTIFICACION', form.C_TIPO_IDENTIFICACION) ||
                          setFac(i,'APELLIDOS', ((form.APELLIDO_PATERNO||'')+' '+(form.APELLIDO_MATERNO||'')).trim()) ||
                          setFac(i,'NOMBRES', ((form.PRIMER_NOMBRE||'')+' '+(form.SEGUNDO_NOMBRE||'')).trim()) ||
                          setFac(i,'TELEFONO', form.TELEFONO||'') ||
                          setFac(i,'EMAIL', form.EMAIL||'') ||
                          setFac(i,'DIRECCION', form.DIRECCION_DOMICILIO||'')
                        }>
                        Copiar
                      </button>
                    </td>
                    <td style={tdStyle}>
                      <select style={selStyle} value={f.C_TIPO_IDENTIFICACION||''} onChange={e => setFac(i,'C_TIPO_IDENTIFICACION',e.target.value)}>
                        <option value="">Seleccione</option>
                        {TIPOS_ID.map(t => <option key={t.v} value={t.v}>{t.l}</option>)}
                      </select>
                    </td>
                    <td style={tdStyle}>
                      <input style={inpStyle} value={f.IDENTIFICACION||''} placeholder="Identificación"
                        onChange={e => setFac(i,'IDENTIFICACION',e.target.value)} />
                      {f.C_TIPO_IDENTIFICACION === 'CDL' && (
                        <>
                          <button type="button" style={{marginTop:3,fontSize:10,padding:'2px 6px',cursor:'pointer'}}
                            className="btn btn-sm"
                            disabled={buscandoCedulaFac===i || !/^\d{10}$/.test(f.IDENTIFICACION||'')}
                            onClick={() => buscarPorCedulaFac(i)}>
                            {buscandoCedulaFac===i ? 'Buscando...' : '🔍 Autocompletar'}
                          </button>
                          {msgCedulaFac[i] && <div style={{fontSize:10,marginTop:2,color:msgCedulaFac[i].startsWith('✓')?'var(--green)':'var(--red)'}}>{msgCedulaFac[i]}</div>}
                        </>
                      )}
                      <input style={{...inpStyle,marginTop:3}} value={f.APELLIDOS||''} placeholder="Apellidos"
                        onChange={e => setFac(i,'APELLIDOS',e.target.value)} />
                      <input style={{...inpStyle,marginTop:3}} value={f.NOMBRES||''} placeholder="Nombres / Razón social"
                        onChange={e => setFac(i,'NOMBRES',e.target.value)} />
                    </td>
                    <td style={tdStyle}>
                      <input style={inpStyle} value={f.TELEFONO||''} placeholder="Teléfono"
                        onChange={e => setFac(i,'TELEFONO',e.target.value)} />
                    </td>
                    <td style={tdStyle}>
                      <input style={inpStyle} value={f.DIRECCION||''} placeholder="Dirección"
                        onChange={e => setFac(i,'DIRECCION',e.target.value)} />
                    </td>
                    <td style={tdStyle}>
                      <input style={inpStyle} type="email" value={f.EMAIL||''} placeholder="Email"
                        onChange={e => setFac(i,'EMAIL',e.target.value)} />
                    </td>
                  </tr>
                ))}
                {facturacion.length === 0 && (
                  <tr><td colSpan={7} style={{textAlign:'center',padding:'24px 0',color:'var(--text3)',fontSize:12}}>
                    Sin datos de facturación — presione + para agregar
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
    </>
  );
}

/* ===========================================
   PÁGINA PRINCIPAL
=========================================== */
export default function Pacientes() {
  const navigate = useNavigate();
  const [modo, setModo]               = useState('lista');
  const [pacSelected, setPacSelected] = useState(null);

  const abrirEdicion = async (p) => {
    try {
      // Cargamos la ficha completa para tener COD_PROVINCIA y COD_CANTON del JOIN
      const { data } = await api.get(`/pacientes/${p.COD}`);
      setPacSelected(data);
    } catch {
      setPacSelected(p);
    }
    setModo('form');
  };

  return (
    <>
      <style>{css}</style>
      <div className="fade-up">
        {modo === 'lista' && (
          <ListaPacientes
            onNuevo={() => { setPacSelected(null); setModo('form'); }}
            onEditar={abrirEdicion}
            onHistoria={p => navigate(`/historia?paciente=${p.COD}`)}
            onImprimir={imprimirHCU}
          />
        )}
        {modo === 'form' && (
          <FormPaciente
            paciente={pacSelected}
            onVolver={() => setModo('lista')}
            onGuardado={() => setModo('lista')}
            onVerFicha={cod => abrirEdicion({ COD: cod })}
          />
        )}
      </div>
    </>
  );
}
